import { Injectable, OnDestroy, signal, WritableSignal, Signal, inject, computed } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { EMPTY, Observable, defer, from, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BACKGROUND_REQUEST, EnvironmentService, SKIP_PROBLEM_HANDLER } from '@lib/shared/data-access';
import { SignalRClientConnectionInfoModel } from '@lib/signalr-models';
import { NotificationAction } from '@lib/notification/models';
import { FeatureToggleService } from '@lib/feature-toggle/data-access';
import { FeatureToggleType } from '@lib/feature-toggle/models';

/**
 * The waits before each restart of a connection that closed for good: 5 s, 15 s, 30 s, then every minute until one
 * connects.
 */
const RESTART_DELAYS_MS = [5_000, 15_000, 30_000, 60_000];

/**
 * Each restart delay moves up to this fraction either way at random, so the windows a hub outage dropped do not all
 * come back on the same second.
 */
const RESTART_JITTER = 0.2;

/**
 * This window's Notifications Hub ("MainHub") connection: the bell's notifications, the force-sync push, and the
 * connection id the offline-sync check-in reports.
 *
 * KEPT UP. `withAutomaticReconnect()` gives up after its last retry (0, 2, 10 and 30 s — about 42 s), and the closed
 * connection then stays closed. So a connection that closes for any reason but {@link stopConnection} is restarted
 * with a FULL start — a new negotiate, since the serverless access token it handed out expires — after
 * {@link RESTART_DELAYS_MS} (with {@link RESTART_JITTER}), and at once when the browser comes back online. While
 * offline no timer tries; the `online` event does. A start that fails, or ends without connecting, is restarted the
 * same way — except when the negotiate is refused for AUTH (401, 403, or Auth0 asking for a login or consent): that
 * is final until the next {@link startConnection}, or every restart would run the auth interceptor's logout again.
 *
 * NEVER USER ACTIVITY. Every negotiate is a `BACKGROUND_REQUEST`, so a connection kept up for hours never holds off
 * the idle logout (the CJIS session lock).
 *
 * LISTENERS OUTLIVE CONNECTIONS. Every handler {@link on} holds is attached to each connection as it is built, so a
 * restart (or any second start) never silently drops one.
 */
@Injectable({
  providedIn: 'root'
})
export class SignalRService implements OnDestroy {
  private hubConnection?: signalR.HubConnection;
  private environmentService = inject(EnvironmentService);
  private featureToggleService = inject(FeatureToggleService);

  public isNotificationsEnabledComputedSignal = computed(() => { return this.featureToggleService.tenantFeaturesComputedSignal().some(feature => feature.id === FeatureToggleType.Notifications && feature.isEnabled) })

  public muteTimers: { [key: string]: number } = {};
    // how long to mute different notifications based on their frequency
  public muteDurations: { [key in NotificationAction]?: number } = {
    [NotificationAction.Created]: 10000, // 10 seconds
    [NotificationAction.Completed]: 10000, // 10 seconds
    [NotificationAction.Abandoned]: 10000, // 10 seconds
    [NotificationAction.NewComment]: 60000, // 60 seconds
    [NotificationAction.CommentResolved]: 60000, // 60 seconds
    [NotificationAction.LevelApproved]: 10000, // 10 seconds

    [NotificationAction.Acknowledged]: 10000, // 10 seconds
    [NotificationAction.Reassigned]: 10000, // 10 seconds
    [NotificationAction.Deleted]: 10000, // 10 seconds
    [NotificationAction.Updated]: 10000, // 10 seconds
  };

  /**
   * Every listener {@link on} holds open, until its subscriber lets go — attached to each NEW connection as it is
   * built, not only the ones that arrived before the first, so the bell and the force-sync push survive a restart.
   */
  private listeners: { eventName: string; handler: (...args: any[]) => void }[] = [];

  /** The start under way (negotiate, then connect), shared, so two callers never build two connections at once. */
  private startInFlight: Observable<void> | null = null;

  /**
   * Set by {@link stopConnection} (sign-out), and by a negotiate refused for auth: nothing restarts the connection
   * until the next {@link startConnection}.
   */
  private stopRequested = false;

  /**
   * Bumped by every {@link stopConnection}. A start begun before it ends quietly: it never connects, never schedules a
   * restart, and never touches the state a later start owns.
   */
  private stopGeneration = 0;

  /**
   * A start asked for while the connection was still going down (`Disconnecting`, after a stop): made the moment it is
   * down rather than after a backoff, and never beside it.
   */
  private startWhenDisconnected = false;

  private restartTimer: ReturnType<typeof setTimeout> | null = null;

  /** Restarts tried since the connection was last up, which picks the wait from {@link RESTART_DELAYS_MS}. */
  private restartAttempt = 0;

  private _connectionState: WritableSignal<signalR.HubConnectionState> = signal(signalR.HubConnectionState.Disconnected);

  public connectionState: Signal<signalR.HubConnectionState> = this._connectionState.asReadonly();

  private _connectionId: WritableSignal<string | null> = signal<string | null>(null);

  /**
   * This window's connection id on the Notifications Hub while it is connected; null before the first start, while it
   * reconnects, and once it is stopping or closed. A reconnect gets a NEW id, and so does a restart. Every Pathfinder
   * window holds its own connection.
   *
   * The offline-sync check-in sends it, so the API can ask Azure SignalR whether this device is still online.
   */
  public connectionId: Signal<string | null> = this._connectionId.asReadonly();

  private http = inject(HttpClient);

  public isNotificationMuted(action: NotificationAction, currentTimestamp: number): boolean {
    return (
        !!this.muteTimers[action] &&
        currentTimestamp < (this.muteTimers[action] ?? 0)
    );
  }
  
  public setNotificationMuteTimer(action: NotificationAction, currentTimestamp: number): void {
      const muteDuration = this.muteDurations[action] || 10000; // Default to 10 seconds
      this.muteTimers[action] = currentTimestamp + muteDuration;
  }

  /** Set once the first start has run, so the `online` listener is installed once. */
  private onlineListenerAttached = false;

  /**
   * The device has network again: connect NOW — a start deferred while offline, or a restart that would otherwise
   * wait out its backoff. Nothing while connected, nor after {@link stopConnection}.
   */
  private readonly onOnline = () => {
    if (this.stopRequested) {
      return;
    }
    this.clearRestartTimer();
    this.restartAttempt = 0;
    this.ensureConnection().subscribe({ error: () => undefined });
  };

  /**
   * Whether a negotiate is worth attempting at all.
   *
   * `navigator.onLine` reports the ADAPTER rather than reachability, so it is trusted in one direction only:
   * a false means nothing can possibly connect, a true is merely permission to try.
   */
  private canReachNetwork(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  /** Listen for the network coming back, once, so a start deferred while offline is not simply lost. */
  private attachOnlineListener(): void {
    if (this.onlineListenerAttached || typeof window === 'undefined') {
      return;
    }
    this.onlineListenerAttached = true;
    window.addEventListener('online', this.onOnline);
  }

  /**
   * Connects this window to the Notifications Hub, and keeps it connected from then on (see the class) until
   * {@link stopConnection}.
   *
   * Safe to call again: the shell's effect does, on every auth change. While a start is under way the caller shares
   * it, and while a connection is up (or reconnecting on its own) nothing happens — never a second connection. A
   * connection still going down after a stop is started again the moment it is down.
   */
  public startConnection(): Observable<void> {
    return defer(() => {
      // An explicit start (sign-in) re-arms what a stop disarmed, and starts the backoff over: it is not a restart.
      this.stopRequested = false;
      this.restartAttempt = 0;
      return this.ensureConnection();
    });
  }

  /** The one way a connection is started, by a caller or a restart: at most one start at a time, and one connection. */
  private ensureConnection(): Observable<void> {
    if (this.startInFlight) {
      return this.startInFlight;
    }
    // Still going down after a stop: started the moment it is down (its close, or its stopped start ending), rather
    // than after a backoff — and never beside it.
    if (this.hubConnection?.state === signalR.HubConnectionState.Disconnecting) {
      this.startWhenDisconnected = true;
      return of(undefined);
    }
    // Connected, connecting, or reconnecting on its own: a second connection would only leak this one.
    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      return of(undefined);
    }

    // The network coming back starts it at once, whatever happens below.
    this.attachOnlineListener();

    // NOT WHILE OFFLINE. This is called from an effect that re-runs on auth-signal changes, so with no
    // network each run added a failed negotiate and a failed token fetch — the pairs filling the network log
    // in the offline screenshot. EMPTY rather than an error: a caller subscribing with no error handler must
    // not take an unhandled rejection for a condition that is not a fault.
    if (!this.canReachNetwork()) {
      this._connectionState.set(signalR.HubConnectionState.Disconnected);
      return EMPTY;
    }

    this.clearRestartTimer();
    // Shared, and run to the end even when its caller lets go, so a second caller joins it instead of starting
    // another — and the negotiate is never abandoned halfway.
    const start: Observable<void> = this.connect().pipe(
      finalize(() => {
        if (this.startInFlight === start) {
          this.startInFlight = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.startInFlight = start;
    return start;
  }

  /**
   * A NEW connection: a fresh negotiate (the access token it hands out expires, so an earlier one's cannot be
   * reused), every listener attached, then started. A failure — or a start that ends without connecting — schedules
   * the next restart ({@link startDidNotConnect}).
   *
   * The negotiate opts out of the shared Problem handler (`SKIP_PROBLEM_HANDLER`): left to it, a Problem answer would
   * become a toast and an empty, completed stream — no error here, so no restart — and the connection would stay
   * down for good. It is a `BACKGROUND_REQUEST`: the app keeps this connection up, the officer does not.
   */
  private connect(): Observable<void> {
    const negotiateUrl = `${this.environmentService.notificationsHubAzureFunctionUrl}/api/signalr/negotiate`;
    const context = new HttpContext().set(SKIP_PROBLEM_HANDLER, true).set(BACKGROUND_REQUEST, true);
    const generation = this.stopGeneration;
    let connection: signalR.HubConnection | undefined;
    let connected = false;

    return this.http.post<SignalRClientConnectionInfoModel>(negotiateUrl, null, { context })
        .pipe(
            switchMap(connectionInfo => {
                // Stopped (signed out) while the negotiate was out: nothing to connect.
                if (generation !== this.stopGeneration) {
                    return EMPTY;
                }

                this._connectionState.set(signalR.HubConnectionState.Connecting);

                const built = new signalR.HubConnectionBuilder()
                .withUrl(connectionInfo.url, {
                    accessTokenFactory: () => connectionInfo.accessToken
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Information)
                .build();
                connection = built;

                // REPLACED, NEVER LEFT RUNNING BESIDE IT. The old one is down already (a start only gets here
                // without a live connection); stopping it makes sure. It is stopped AFTER this one is current, so
                // its close cannot be taken for this one's.
                const previous = this.hubConnection;
                this.hubConnection = built;
                if (previous) {
                    void previous.stop().catch(() => undefined);
                }

                this.registerLifecycleEvents(built);
                this.attachListeners(built);

                return from(built.start());
            }),
            tap(() => {
                // Stopped while it was starting: the stop closes it, and its close says the rest.
                if (generation !== this.stopGeneration || !connection || connection !== this.hubConnection) {
                    return;
                }
                connected = true;
                this._connectionState.set(signalR.HubConnectionState.Connected);
                this._connectionId.set(connection.connectionId ?? null);
                this.restartAttempt = 0;
                console.log('SignalR Connected to MainHub');
            }),
            catchError(err => {
                console.error('Error while establishing connection:', err);
                this.startDidNotConnect(generation, connection, err);
                return throwError(() => err);
            }),
            // ENDED WITHOUT CONNECTING and without an error — the negotiate answered nothing (an interceptor swallowed
            // it), or the start was stopped. A failure all the same, unless it was stopped on purpose.
            tap({
                complete: () => {
                    if (!connected) {
                        this.startDidNotConnect(generation, connection, null);
                    }
                },
            }),
        );
  }

  /**
   * A start that did not connect: tried again on the same backoff as a connection that closed. Not when the auth
   * refused it ({@link isFinalAuthFailure}): that waits for the next {@link startConnection}. And not when it was
   * stopped on purpose meanwhile — except for a start asked for since, waiting for this connection to be down.
   *
   * @param connection the connection this start built, if it got that far.
   * @param error why it failed; null for a start that simply ended.
   */
  private startDidNotConnect(generation: number, connection: signalR.HubConnection | undefined, error: unknown): void {
    if (generation !== this.stopGeneration) {
      // Its stop has finished it, and no close will say so (a connection that never connected does not fire one).
      if (connection && connection === this.hubConnection && !this.startInFlight) {
        this._connectionState.set(signalR.HubConnectionState.Disconnected);
        this.startIfAskedWhileGoingDown();
      }
      return;
    }
    if (connection && connection !== this.hubConnection) {
      return;
    }
    this._connectionState.set(signalR.HubConnectionState.Disconnected);
    this._connectionId.set(null);
    if (error !== null && SignalRService.isFinalAuthFailure(error)) {
      // FINAL until the next explicit start (the auth effect's, after a sign-in): a restart would only be refused
      // again, and run the auth interceptor's logout and navigation every time it was.
      console.warn('SignalR negotiate refused for auth; not restarting until the next start.');
      this.stopRequested = true;
      return;
    }
    this.scheduleRestart();
  }

  /**
   * A refusal no restart can change: the negotiate answered 401 or 403, or Auth0 needs a login or consent (the auth
   * interceptor has already started the logout).
   */
  private static isFinalAuthFailure(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 401 || error.status === 403;
    }
    const auth0 = (error ?? {}) as { error?: unknown; error_description?: unknown };
    const code = typeof auth0.error === 'string' ? auth0.error : '';
    const description = typeof auth0.error_description === 'string' ? auth0.error_description : '';
    return code === 'login_required' || code === 'consent_required' || description.includes('consent_required');
  }

  /**
   * The connection is down: a start asked for while it was going down ({@link startWhenDisconnected}) runs now.
   *
   * @returns whether one did.
   */
  private startIfAskedWhileGoingDown(): boolean {
    if (!this.startWhenDisconnected) {
      return false;
    }
    this.startWhenDisconnected = false;
    if (this.stopRequested) {
      return false;
    }
    this.ensureConnection().subscribe({ error: () => undefined });
    return true;
  }

  /**
   * The next restart, after {@link RESTART_DELAYS_MS} give or take {@link RESTART_JITTER} — unless the connection was
   * stopped on purpose, one is already due, or the device is offline: then the `online` event starts it
   * ({@link onOnline}), and no timer asks a network that is not there.
   */
  private scheduleRestart(): void {
    if (this.stopRequested || this.restartTimer !== null) {
      return;
    }
    if (!this.canReachNetwork()) {
      this.attachOnlineListener();
      return;
    }

    const base = RESTART_DELAYS_MS[Math.min(this.restartAttempt, RESTART_DELAYS_MS.length - 1)];
    const delay = Math.round(base * (1 - RESTART_JITTER + Math.random() * 2 * RESTART_JITTER));
    this.restartAttempt++;
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (!this.stopRequested) {
        this.ensureConnection().subscribe({ error: () => undefined });
      }
    }, delay);
  }

  private clearRestartTimer(): void {
    if (this.restartTimer !== null) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
  }

  /** Every listener held, onto a connection just built — which has none yet, so none is attached twice. */
  private attachListeners(connection: signalR.HubConnection): void {
    this.listeners.forEach(listener => connection.on(listener.eventName, listener.handler));
  }

  /**
   * Generic helper to listen to ANY specific event name as an Observable.
   *
   * Held for as long as it is subscribed, on the connection there is now and on every one built after it — a
   * subscription made before the first start, or that outlives a restart, misses nothing.
   */
  public on<T>(eventName: string): Observable<T> {
    return new Observable<T>(observer => {
      const listener = { eventName, handler: (data: T) => observer.next(data) };
      this.listeners.push(listener);
      // The connection there is now, if any; every later one gets it from the registry as it is built.
      this.hubConnection?.on(eventName, listener.handler);

      // Teardown (when the subscriber is destroyed): off the current connection, and out of the registry, so no
      // later connection gets it either.
      return () => {
        this.hubConnection?.off(eventName, listener.handler);
        this.listeners = this.listeners.filter(held => held !== listener);
      };
    });
  }

  /** Only the CURRENT connection's events count: one a start has replaced says nothing about this window any more. */
  private registerLifecycleEvents(connection: signalR.HubConnection): void {
    connection.onclose(() => {
      if (connection !== this.hubConnection) {
        return;
      }
      this._connectionState.set(signalR.HubConnectionState.Disconnected);
      this._connectionId.set(null);
      // A start asked for while a stop took it down runs now, rather than after a backoff.
      if (this.startIfAskedWhileGoingDown()) {
        return;
      }
      // Closed for good — the automatic reconnect gave up, or the hub closed it. Restarted, unless it was stopped.
      this.scheduleRestart();
    });
    connection.onreconnecting(() => {
      if (connection === this.hubConnection) {
        this._connectionState.set(signalR.HubConnectionState.Reconnecting);
        // The id is dead now: a check-in while it reconnects must not report it. The reconnect brings a new one.
        this._connectionId.set(null);
      }
    });
    connection.onreconnected((connectionId) => {
      if (connection === this.hubConnection) {
        this._connectionState.set(signalR.HubConnectionState.Connected);
        this._connectionId.set(connectionId ?? connection.connectionId ?? null);
        this.restartAttempt = 0;
      }
    });
  }

  /**
   * Closes the connection ON PURPOSE (sign-out): nothing restarts it — no backoff, no `online` — until the next
   * {@link startConnection}.
   */
  public stopConnection(): Observable<void> {
    this.stopRequested = true;
    this.stopGeneration++;
    this.startWhenDisconnected = false;
    this.clearRestartTimer();
    // A start under way is let go: it ends without connecting (see connect), and the next start is a new one.
    this.startInFlight = null;
    // Going away now, not once it has gone: a check-in meanwhile must not report it.
    this._connectionId.set(null);
    const stopping = this.hubConnection;
    if (stopping) {
      return from(stopping.stop()).pipe(
        tap(() => {
          // Unless a start asked for since has already begun its own (it owns the state now).
          if (stopping === this.hubConnection && !this.startInFlight) {
            this._connectionState.set(signalR.HubConnectionState.Disconnected);
            this._connectionId.set(null);
          }
        })
      );
    }
    return from(Promise.resolve());
  }

  /** The app is going: no restart, no `online` listener, no connection left behind. */
  public ngOnDestroy(): void {
    this.stopRequested = true;
    this.stopGeneration++;
    this.startWhenDisconnected = false;
    this.startInFlight = null;
    this.clearRestartTimer();
    if (this.onlineListenerAttached && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      this.onlineListenerAttached = false;
    }
    void this.hubConnection?.stop().catch(() => undefined);
  }
}